/**
 * Pagination Component
 * 
 * A versatile pagination component for navigating through multiple pages
 * of content with various styles and configurations.
 */
import { cn } from '../../utils/tailwind-utils'

interface PaginationProps {
  /**
   * Current page number (1-based)
   */
  currentPage: number
  
  /**
   * Total number of pages
   */
  totalPages: number
  
  /**
   * Function called when page changes
   */
  onPageChange: (page: number) => void
  
  /**
   * Number of page buttons to show
   * @default 5
   */
  siblingCount?: number
  
  /**
   * Whether to show the first/last page buttons
   * @default true
   */
  showFirstLast?: boolean
  
  /**
   * Whether to show the previous/next page buttons
   * @default true
   */
  showPrevNext?: boolean
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * The visual variant of the pagination
   * @default "default"
   */
  variant?: 'default' | 'outline' | 'minimal'
  
  /**
   * The size of the pagination buttons
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg'
  
  /**
   * Whether the pagination is disabled
   */
  disabled?: boolean
}

/**
 * Pagination component for navigating through multiple pages
 * 
 * @example
 * // Basic usage
 * <Pagination
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPageChange={setCurrentPage}
 * />
 * 
 * @example
 * // With custom configuration
 * <Pagination
 *   currentPage={page}
 *   totalPages={20}
 *   onPageChange={handlePageChange}
 *   siblingCount={2}
 *   variant="outline"
 *   size="sm"
 * />
 * 
 * @example
 * // Minimal variant without first/last buttons
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 *   variant="minimal"
 *   showFirstLast={false}
 * />
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 2,
  showFirstLast = true,
  showPrevNext = true,
  className,
  variant = 'default',
  size = 'md',
  disabled = false,
}: PaginationProps) {
  // Generate page range
  const getPageRange = () => {
    const totalNumbers = siblingCount * 2 + 1
    
    // If the number of pages is less than the page numbers we want to show
    if (totalNumbers >= totalPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages)
    
    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1
    
    // No dots to show, just show the range
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 1 + 2 * siblingCount
      return Array.from({ length: leftItemCount }, (_, i) => i + 1)
    }
    
    // No right dots to show, just show the range
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 1 + 2 * siblingCount
      return Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      )
    }
    
    // Show both left and right dots
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      )
      return [1, 'leftDots', ...middleRange, 'rightDots', totalPages]
    }
    
    return []
  }
  
  const pageRange = getPageRange()
  
  // Size classes
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
  }
  
  // Variant classes
  const getVariantClasses = (isActive: boolean) => {
    const variants = {
      default: isActive
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
      outline: isActive
        ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
        : 'bg-transparent border border-border text-foreground hover:bg-muted',
      minimal: isActive
        ? 'bg-transparent text-primary font-medium hover:bg-muted'
        : 'bg-transparent text-foreground hover:bg-muted',
    }
    
    return variants[variant]
  }
  
  // Base button classes
  const baseButtonClasses = 'flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
  
  // Handle page change
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || disabled) return
    onPageChange(page)
  }
  
  return (
    <nav
      className={cn('flex items-center justify-center space-x-1', className)}
      aria-label="Pagination"
    >
      {/* First page button */}
      {showFirstLast && (
        <button
          className={cn(
            baseButtonClasses,
            sizeClasses[size],
            getVariantClasses(false),
            (currentPage === 1 || disabled) && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || disabled}
          aria-label="Go to first page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <polyline points="11 17 6 12 11 7"></polyline>
            <polyline points="18 17 13 12 18 7"></polyline>
          </svg>
        </button>
      )}
      
      {/* Previous page button */}
      {showPrevNext && (
        <button
          className={cn(
            baseButtonClasses,
            sizeClasses[size],
            getVariantClasses(false),
            (currentPage === 1 || disabled) && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          aria-label="Go to previous page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}
      
      {/* Page numbers */}
      {pageRange.map((page, index) => {
        if (page === 'leftDots' || page === 'rightDots') {
          return (
            <span
              key={`dots-${index}`}
              className="flex items-center justify-center w-10 h-10 text-muted-foreground"
            >
              &hellip;
            </span>
          )
        }
        
        return (
          <button
            key={page}
            className={cn(
              baseButtonClasses,
              sizeClasses[size],
              getVariantClasses(page === currentPage),
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => handlePageChange(page as number)}
            disabled={disabled}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )
      })}
      
      {/* Next page button */}
      {showPrevNext && (
        <button
          className={cn(
            baseButtonClasses,
            sizeClasses[size],
            getVariantClasses(false),
            (currentPage === totalPages || disabled) && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          aria-label="Go to next page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}
      
      {/* Last page button */}
      {showFirstLast && (
        <button
          className={cn(
            baseButtonClasses,
            sizeClasses[size],
            getVariantClasses(false),
            (currentPage === totalPages || disabled) && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || disabled}
          aria-label="Go to last page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <polyline points="13 17 18 12 13 7"></polyline>
            <polyline points="6 17 11 12 6 7"></polyline>
          </svg>
        </button>
      )}
    </nav>
  )
}

export default Pagination
